import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface RematchState {
  hasOffer: boolean;
  isOfferer: boolean;
  isAccepted: boolean;
  rematchSessionId: string | null;
}

export function useRematchSystem(gameSessionId: string | null) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [rematchState, setRematchState] = useState<RematchState>({
    hasOffer: false,
    isOfferer: false,
    isAccepted: false,
    rematchSessionId: null
  });

  // Fetch initial rematch state
  useEffect(() => {
    if (!gameSessionId) return;

    const fetchRematchState = async () => {
      const { data, error } = await supabase
        .from('game_sessions')
        .select('*')
        .eq('id', gameSessionId)
        .single();

      if (!error && data) {
        const gameData = data as any;
        setRematchState({
          hasOffer: !!gameData.rematch_offer_from,
          isOfferer: gameData.rematch_offer_from === user?.id,
          isAccepted: gameData.rematch_accepted || false,
          rematchSessionId: gameData.rematch_session_id
        });
      }
    };

    fetchRematchState();
  }, [gameSessionId, user?.id]);

  // Subscribe to game_sessions changes for real-time updates
  useEffect(() => {
    if (!gameSessionId) return;
    
    const channel = supabase
      .channel(`rematch:${gameSessionId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'game_sessions',
        filter: `id=eq.${gameSessionId}`
      }, (payload) => {
        const game = payload.new as any;
        setRematchState({
          hasOffer: !!game.rematch_offer_from,
          isOfferer: game.rematch_offer_from === user?.id,
          isAccepted: game.rematch_accepted || false,
          rematchSessionId: game.rematch_session_id
        });

        // Show notifications for rematch events
        if (game.rematch_offer_from && game.rematch_offer_from !== user?.id) {
          toast({
            title: "Rematch Requested!",
            description: "Your opponent wants to play again with swapped colors",
          });
        }
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [gameSessionId, user?.id, toast]);

  // Offer rematch
  const offerRematch = useCallback(async () => {
    if (!gameSessionId || !user?.id) return false;
    
    const { error } = await supabase
      .from('game_sessions')
      .update({ rematch_offer_from: user.id } as any)
      .eq('id', gameSessionId);
      
    if (!error) {
      toast({ 
        title: "Rematch offered", 
        description: "Waiting for opponent to accept..." 
      });
    } else {
      toast({
        title: "Failed to offer rematch",
        description: error.message,
        variant: "destructive"
      });
    }
    return !error;
  }, [gameSessionId, user?.id, toast]);

  // Accept rematch
  const acceptRematch = useCallback(async () => {
    if (!gameSessionId) return null;
    
    const { data: newGameId, error } = await (supabase as any).rpc(
      'create_rematch_session',
      { _original_game_id: gameSessionId }
    );
    
    if (!error && newGameId) {
      toast({ 
        title: "Rematch started!", 
        description: "Colors have been swapped. Good luck!" 
      });
      return newGameId;
    } else {
      toast({
        title: "Failed to start rematch",
        description: error?.message || "Unknown error",
        variant: "destructive"
      });
    }
    return null;
  }, [gameSessionId, toast]);

  // Decline rematch
  const declineRematch = useCallback(async () => {
    if (!gameSessionId) return false;
    
    const { error } = await supabase
      .from('game_sessions')
      .update({ rematch_offer_from: null } as any)
      .eq('id', gameSessionId);
      
    if (!error) {
      toast({ title: "Rematch declined" });
    } else {
      toast({
        title: "Failed to decline rematch",
        description: error.message,
        variant: "destructive"
      });
    }
    return !error;
  }, [gameSessionId, toast]);

  return {
    rematchState,
    offerRematch,
    acceptRematch,
    declineRematch
  };
}
